import check from '../libraries/check-types-ext';
import { flatMap } from 'lodash';
import { DomainCoreGeneration } from '../libraries/DomainCoreGeneration';
import OrderHelperObject from './OrderHelperObject';
import ReceiptLine, { ReceiptLineType } from './ReceiptLine';
export default class OrderLine2 {
    constructor({ type, productId, rootProductId, quantity, price, name, ean, plu, subLines, unitType, productIngredients, selectedOption, unselectedOption, options, discount, includedInMainPrice = false, generation = DomainCoreGeneration.Generation1, voucherId, addedByGame = false, }) {
        const args = {
            type,
            productId,
            quantity,
            price,
            name,
            ean,
            subLines,
            plu,
            unitType,
            productIngredients,
            options,
        };
        check.string.orFail(type, 'type');
        check.string.orFail(name, 'name');
        if (type === OrderLineType.Product) {
            if (typeof price !== 'number') {
                price = 0;
            }
            if (price > 0) {
                check.string.orFail(unitType, 'unitType', args);
            }
        }
        if (type === OrderLineType.Aggregation || type === OrderLineType.Config) {
            check.undefined.orFail(price);
            check.undefined.orFail(unitType);
        }
        if (type === OrderLineType.Aggregation) {
            check.string.orFail(rootProductId);
        }
        if (typeof price === 'number' && price > 0 && type !== OrderLineType.Delivery) {
            check.string.orFail(unitType, 'unitType');
        }
        this.type = type;
        if (productId)
            this.productId = productId;
        if (rootProductId)
            this.rootProductId = rootProductId;
        if (quantity !== undefined)
            this.quantity = quantity;
        if (price !== undefined)
            this.price = price;
        this.includedInMainPrice = includedInMainPrice;
        if (productIngredients)
            this.productIngredients = productIngredients;
        if (name)
            this.name = name ? name.replace('undefined', '') : '';
        if (unitType)
            this.unitType = unitType;
        if (ean)
            this.ean = ean;
        if (plu)
            this.plu = plu;
        if (options)
            this.options = options;
        if (selectedOption)
            this.selectedOption = selectedOption;
        if (unselectedOption)
            this.unselectedOption = unselectedOption;
        if (discount)
            this.discount = discount;
        if (voucherId)
            this.voucherId = voucherId;
        if (generation)
            this.generation = generation;
        if (addedByGame)
            this.addedByGame = addedByGame;
        if (!subLines || subLines.length === 0) {
            this.subLines = [];
        }
        else {
            if (subLines[0].constructor !== OrderLine2) {
                this.subLines = subLines.filter((line) => line.type).map((line) => new OrderLine2(line));
            }
            else {
                this.subLines = subLines;
            }
        }
    }
    getTotalQuantity() {
        return OrderHelperObject.getTotalQuantityForOneOrderLine(this);
    }
    getTotalPrice() {
        if (Number(this.generation) >= DomainCoreGeneration.Generation3) {
            return this.totalPriceAccum;
        }
        if (this.includedInMainPrice) {
            return 0;
        }
        else {
            let mainPrice = 0;
            if (this.price && this.quantity) {
                mainPrice = Math.round(this.price * 100) * this.quantity;
            }
            const subLinePrice = this.getSubLineTotalPrice() * 100;
            const baseLinePrice = mainPrice + subLinePrice;
            return (baseLinePrice + this.getConfigPriceModifier() * 100) / 100;
        }
    }
    getConfigPriceModifier() {
        const result = this.subLines
            .filter((line) => line.type === OrderLineType.Config && line.selectedOption)
            .reduce((carry, line) => {
            if (Array.isArray(line.selectedOption)) {
                line.selectedOption.forEach((opt) => {
                    if (opt.priceModifier) {
                        carry += Math.round((opt.priceModifier.value || 0) * 100);
                    }
                });
            }
            else if (line.selectedOption.priceModifier) {
                carry += Math.round((line.selectedOption.priceModifier.value || 0) * 100);
            }
            return carry;
        }, 0);
        if (result !== 0) {
            return result / 100;
        }
        else {
            return 0;
        }
    }
    getSubLineTotalPrice() {
        if (this.subLines && this.subLines.length > 0) {
            return this.subLines.reduce((carry, line) => carry + line.getTotalPrice(), 0);
        }
        else {
            return 0;
        }
    }
    getReceiptLine({ groupStart = true, orderIndex, absoluteRootProductId }) {
        const getProductConfigurations = () => {
            return this.subLines
                .filter((line) => line.type === OrderLineType.Config)
                .map((configLine) => {
                return {
                    name: configLine.name,
                    selectedOption: configLine.selectedOption,
                };
            });
        };
        if (this.type === OrderLineType.Aggregation) {
            return new ReceiptLine({
                orderIndex,
                type: ReceiptLineType.Section,
                groupStart: groupStart,
                name: this.name,
                options: this.options,
                productId: this.productId,
                discount: this.discount,
                addedByGame: this.addedByGame,
                absoluteRootProductId,
                productConfigurations: getProductConfigurations(),
                quantity: undefined,
                amount: undefined,
                description: undefined,
                price: undefined,
                ean: undefined,
                plu: undefined,
                totalPrice: undefined,
                unitType: undefined,
                rootProductId: undefined,
                configPriceModifier: undefined,
            });
        }
        else if (this.type === OrderLineType.Delivery) {
            return new ReceiptLine({
                productConfigurations: [],
                orderIndex,
                type: ReceiptLineType.Delivery,
                quantity: this.quantity,
                groupStart: groupStart,
                name: this.name,
                price: this.price,
                ean: this.ean,
                plu: this.plu,
                unitType: this.unitType,
                totalPrice: this.getTotalPrice(),
                amount: undefined,
                description: undefined,
                productId: undefined,
                rootProductId: undefined,
                configPriceModifier: undefined,
                absoluteRootProductId: undefined,
                options: undefined,
                discount: undefined,
            });
        }
        else if (this.type === OrderLineType.ReturnableItem) {
            return new ReceiptLine({
                productConfigurations: [],
                orderIndex,
                type: ReceiptLineType.ReturnableItem,
                quantity: this.quantity,
                groupStart: groupStart,
                options: this.options,
                name: this.name,
                price: this.price,
                ean: this.ean,
                discount: this.discount,
                addedByGame: this.addedByGame,
                plu: this.plu,
                unitType: this.unitType,
                totalPrice: this.getTotalPrice(),
                configPriceModifier: undefined,
                amount: undefined,
                description: undefined,
                productId: undefined,
                rootProductId: undefined,
                absoluteRootProductId: undefined,
            });
        }
        else {
            const formatAmount = (amount, quantity) => {
                if (quantity) {
                    return Math.round(parseFloat(amount) * quantity * 100) / 100;
                }
                else {
                    return Math.round(parseFloat(amount) * 100) / 100;
                }
            };
            const ingredientsDescription = () => {
                if (Array.isArray(this.productIngredients) && this.productIngredients.length > 1) {
                    return this.productIngredients.map((pi) => {
                        return {
                            text: pi.ingredient ? pi.ingredient.name : 'Unknown ingredient - please contact administrator for help',
                            amount: `${formatAmount(pi.amount, this.quantity)} ${pi.ingredient ? pi.ingredient.unit : 'Unknown ingredient - please contact administrator for help'}`,
                            depth: 0,
                        };
                    });
                }
                else {
                    return [];
                }
            };
            const quantity = this.quantity;
            const subLinesDescription = this.subLines
                ? flatMap(this.subLines, (line) => line.getDescription({
                    quantity,
                    absoluteRootProductId,
                }))
                : [];
            const description = [...ingredientsDescription(), ...subLinesDescription];
            return new ReceiptLine({
                orderIndex,
                type: ReceiptLineType.Product,
                quantity: this.quantity,
                groupStart: groupStart,
                name: this.name,
                amount: (() => {
                    if (Array.isArray(this.productIngredients) && this.productIngredients.length === 1) {
                        const pi = this.productIngredients[0];
                        if (pi.ingredient) {
                            return `${formatAmount(pi.amount, this.quantity)} ${pi.ingredient.unit}`;
                        }
                        else {
                            return `${formatAmount(pi.amount, this.quantity)}, Unknown ingredient - please contact administrator for help!`;
                        }
                    }
                    else {
                        return '';
                    }
                })(),
                productId: this.productId,
                rootProductId: this.rootProductId,
                absoluteRootProductId,
                price: this.price,
                ean: this.ean,
                plu: this.plu,
                discount: this.discount,
                addedByGame: this.addedByGame,
                options: this.options,
                unitType: this.unitType,
                configPriceModifier: this.getConfigPriceModifier(),
                totalPrice: this.getTotalPrice(),
                description: description.length > 0 ? description : '',
                productConfigurations: getProductConfigurations(),
            });
        }
    }
    getDescription({ quantity, absoluteRootProductId, depth = 0, changeFont = false }) {
        const formatAmount = (amount, quantity) => {
            if (quantity) {
                return Math.round(parseFloat(amount) * quantity * 100) / 100;
            }
            else {
                return Math.round(parseFloat(amount) * 100) / 100;
            }
        };
        const ingredientsDescription = (depth) => {
            if (Array.isArray(this.productIngredients) && this.productIngredients.length > 1) {
                return this.productIngredients.map((pi) => {
                    return {
                        text: pi.ingredient ? pi.ingredient.name : 'Unknown ingredient - please contact administrator for help',
                        amount: `${formatAmount(pi.amount, quantity)} ${pi.ingredient ? pi.ingredient.unit : 'Unknown ingredient - please contact administrator for help'}`,
                        depth: depth,
                    };
                });
            }
            else {
                return [];
            }
        };
        const quantityText = typeof this.quantity === 'number' ? `${this.quantity} x ` : '';
        const productQuantity = this.options && this.options.countPerAmount && !this.includedInMainPrice && typeof this.quantity === 'number'
            ? this.quantity
            : quantity;
        const pricePerSubline = this.price && this.includedInMainPrice !== true ? this.price : null;
        const totalSublinePrice = pricePerSubline ? formatAmount(this.price, productQuantity) : null;
        const unitType = pricePerSubline ? this.unitType : null;
        const descriptionLine = {
            text: quantityText + this.name,
            amount: (() => {
                if (Array.isArray(this.productIngredients) && this.productIngredients.length === 1) {
                    const pi = this.productIngredients[0];
                    if (pi.ingredient) {
                        return `${formatAmount(pi.amount, quantity)} ${pi.ingredient.unit}`;
                    }
                    else {
                        return `${formatAmount(pi.amount, quantity)}, Unknown ingredient - please contact administrator for help!`;
                    }
                }
                else {
                    return '';
                }
            })(),
            depth: depth,
            changeFont: changeFont,
            pricePerSubline,
            totalSublinePrice,
            price: pricePerSubline,
            unitType,
            quantity: typeof this.quantity === 'number' ? this.quantity : null,
            productId: this.productId ? this.productId : null,
            ean: this.ean ? this.ean : null,
            plu: this.plu ? this.plu : null,
            absoluteRootProductId: absoluteRootProductId ? absoluteRootProductId : null,
            rootProductId: this.rootProductId ? this.rootProductId : null,
        };
        const ingredientsDescriptions = ingredientsDescription(depth + 1);
        const emptyValues = [null, undefined, ''];
        if (emptyValues.includes(this.name)) {
            const subDescriptions = this.subLines && this.subLines.length
                ? flatMap(this.subLines, (line) => line.getDescription({
                    quantity: quantity,
                    depth,
                    absoluteRootProductId,
                    changeFont: true,
                }))
                : [];
            return [...ingredientsDescriptions, ...subDescriptions];
        }
        else {
            const subDescriptions = this.subLines && this.subLines.length
                ? flatMap(this.subLines, (line) => line.getDescription({
                    quantity,
                    depth: depth + 1,
                    absoluteRootProductId,
                }))
                : [];
            return [descriptionLine, ...ingredientsDescriptions, ...subDescriptions];
        }
    }
}
export var OrderLineType;
(function (OrderLineType) {
    OrderLineType["Product"] = "Product";
    OrderLineType["Config"] = "Config";
    OrderLineType["Aggregation"] = "Aggregation";
    OrderLineType["ReturnableItem"] = "ReturnableItem";
    OrderLineType["Delivery"] = "Delivery";
    OrderLineType["Discount"] = "Discount";
    OrderLineType["CombinatorLine"] = "CombinatorLine";
})(OrderLineType || (OrderLineType = {}));
export const OrderLineHeatBoxDefinition = Object.freeze({
    type: OrderLineType.ReturnableItem,
    name: 'Depositum for termokasser',
    ean: '5710405560867',
    price: 200,
    unitType: 'order',
    quantity: 1,
});
export const OrderLineDriveInDefinition = Object.freeze({
    type: OrderLineType.Delivery,
    name: 'Drive-in delivery',
    ean: '5712872794790',
    price: 0,
    unitType: 'order',
    quantity: 1,
});
export const OrderLineHomeDeliveryDefinition = Object.freeze({
    type: OrderLineType.Delivery,
    name: 'Hjemmelevering',
    ean: '5712876288585',
    price: 68,
    unitType: 'order',
    quantity: 1,
});
